import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { api } from "../../api/client";
import { useAppState } from "../../state/appState";
import { useToasts } from "../../state/toasts";
import { Empty } from "../../ui/components";

/**
 * Оформление витрины: то, что гость видит, открыв меню по QR-коду.
 *
 * Отдельный экран от настроек точки. Там режим работы, минимальная сумма и
 * реквизиты кассы — ошибка останавливает продажи. Здесь вывеска: название,
 * подпись, цвета. Её меняют к сезону и не боятся сломать, поэтому и живёт она
 * отдельно, с предпросмотром вместо предупреждений.
 *
 * Полей здесь немного, и это следствие того, как витрину открывают. Гость
 * стоит на улице с телефоном в руке и уже решил заказать: он пришёл по коду
 * с лавочки, а не с рекламы. Первый экран с крупным заголовком и тремя
 * доводами продавал бы тому, кто уже купил, — поэтому витрина начинается
 * прямо с меню, а от владельца ей нужны только вывеска и цвет. Объявления
 * и акции живут на соседнем экране, среди баннеров.
 */

interface StorefrontDto {
  logoUrl: string | null;
  title: string;
  tagline: string;
  footerText: string;
  colors: { bg: string | null; accent: string | null; ink: string | null };
}

/** Значения по умолчанию совпадают со светлой витриной — это её цвета. */
const FALLBACK = { bg: "#ffffff", accent: "#ff6a1a", ink: "#1b1e20" };

export function StorefrontPage() {
  const { snapshot } = useAppState();
  const { run, notify } = useToasts();

  const source = snapshot?.storefront as StorefrontDto | undefined;

  const [form, setForm] = useState<StorefrontDto | null>(null);
  const [uploading, setUploading] = useState(false);

  /*
   * Форма заполняется один раз. Живой поток событий приходит на каждое
   * изменение в системе, и переливать его в поля значило бы затирать то, что
   * владелец печатает прямо сейчас.
   */
  useEffect(() => {
    if (source && !form) {
      setForm({
        logoUrl: source.logoUrl,
        title: source.title,
        tagline: source.tagline,
        footerText: source.footerText,
        colors: source.colors
      });
    }
  }, [source, form]);

  if (!form) return <Empty>Загружаем оформление…</Empty>;

  function set<K extends keyof StorefrontDto>(key: K, value: StorefrontDto[K]) {
    setForm(current => (current ? { ...current, [key]: value } : current));
  }

  function setColor(key: keyof StorefrontDto["colors"], value: string | null) {
    setForm(current => (current ? { ...current, colors: { ...current.colors, [key]: value } } : current));
  }

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await api.upload<{ url: string }>("/api/admin/catalog/images", file);
      set("logoUrl", result.url);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить картинку", "error");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!form) return;

    await run(
      () =>
        api.patch("/api/admin/storefront", {
          logoUrl: form.logoUrl,
          title: form.title,
          tagline: form.tagline,
          footerText: form.footerText,
          colorBg: form.colors.bg,
          colorAccent: form.colors.accent,
          colorInk: form.colors.ink
        }),
      "Оформление сохранено"
    );
  }

  const accent = form.colors.accent ?? FALLBACK.accent;

  return (
    <form onSubmit={save} className="grid-forms">
      <div className="page-head">
        <div>
          <p className="eyebrow">Витрина</p>
          <h1>Оформление меню</h1>
        </div>
        <button type="submit" className="btn btn-primary">
          Сохранить
        </button>
      </div>

      <section className="panel">
        <header>
          <div>
            <h2>Шапка</h2>
            <p>Логотип и название видит каждый, кто открыл меню по QR-коду.</p>
          </div>
        </header>

        <div className="field-row">
          <label className="field">
            <span>Название</span>
            <input value={form.title} onChange={event => set("title", event.target.value)} maxLength={80} />
          </label>

          <label className="field">
            <span>Подпись под названием</span>
            <input value={form.tagline} onChange={event => set("tagline", event.target.value)} maxLength={160} />
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span>Логотип</span>
            <input type="file" accept="image/*" onChange={event => void upload(event)} disabled={uploading} />
          </label>

          {form.logoUrl ? (
            <div className="row-actions">
              <img src={form.logoUrl} alt="" className="storefront-logo" />
              <button type="button" className="btn btn-sm" onClick={() => set("logoUrl", null)}>
                Убрать логотип
              </button>
            </div>
          ) : (
            <p className="tiny muted">
              Пока логотипа нет, в шапке стоит одно название — это нормально и выглядит опрятнее заглушки.
            </p>
          )}
        </div>

        <label className="field">
          <span>Подпись в подвале</span>
          <input value={form.footerText} onChange={event => set("footerText", event.target.value)} maxLength={240} />
          <small className="tiny muted">
            Стоит под меню. Место для реквизитов и предупреждений, а не для рекламы: объявления — это баннеры.
          </small>
        </label>
      </section>

      <section className="panel">
        <header>
          <div>
            <h2>Цвета</h2>
            <p>
              Три цвета, из которых выводится вся остальная палитра витрины. Двадцать пипеток дали бы
              нечитаемую страницу быстрее, чем красивую.
            </p>
          </div>
        </header>

        <div className="field-row">
          <ColorField
            label="Фон"
            value={form.colors.bg}
            fallback={FALLBACK.bg}
            onChange={value => setColor("bg", value)}
          />
          <ColorField
            label="Акцент"
            value={form.colors.accent}
            fallback={FALLBACK.accent}
            onChange={value => setColor("accent", value)}
          />
          <ColorField
            label="Текст"
            value={form.colors.ink}
            fallback={FALLBACK.ink}
            onChange={value => setColor("ink", value)}
          />
        </div>

        {/* Предпросмотр показывает ровно то сочетание, которое сейчас в полях:
            подобрать цвета вслепую и увидеть результат у гостя — плохой обмен.
            Цвет подписи на кнопке витрина считает по яркости заливки сама,
            поэтому здесь он не задаётся, а повторяет тот же расчёт. */}
        <div
          className="storefront-preview"
          style={{ background: form.colors.bg ?? FALLBACK.bg, color: form.colors.ink ?? FALLBACK.ink }}
        >
          <b>{form.title || "Название"}</b>
          <span>{form.tagline || "Подпись под названием"}</span>
          <button type="button" style={{ background: accent, color: readableOn(accent) }}>
            от 320 ₽
          </button>
        </div>
      </section>
    </form>
  );
}

/**
 * Цвет подписи поверх заливки: белый на тёмной, угольный на светлой.
 *
 * Повторяет расчёт витрины. Порог взят по воспринимаемой яркости, а не по
 * среднему трёх каналов: глаз считает зелёный вчетверо ярче синего, и по
 * среднему насыщенно-зелёная кнопка выходит «тёмной», получает белую подпись
 * и становится нечитаемой.
 */
function readableOn(hex: string): string {
  const value = hex.replace("#", "");
  if (value.length !== 6) return "#ffffff";

  const channel = (at: number) => parseInt(value.slice(at, at + 2), 16) / 255;
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);

  return luminance > 0.6 ? "#1b1e20" : "#ffffff";
}

/** Пипетка с подписью и сбросом. Тем же полем красят баннеры. */
export function ColorField({
  label,
  value,
  fallback,
  onChange
}: {
  label: string;
  value: string | null;
  fallback: string;
  onChange: (value: string | null) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <div className="row-actions">
        <input
          type="color"
          value={value ?? fallback}
          onChange={event => onChange(event.target.value)}
          className="color-input"
        />
        <code className="tiny">{value ?? `${fallback} по умолчанию`}</code>
        {value ? (
          <button type="button" className="btn btn-sm" onClick={() => onChange(null)}>
            Сбросить
          </button>
        ) : null}
      </div>
    </label>
  );
}
